/**
 * src/components/spatial/SpatialDeckOverlay.tsx
 *
 * Mounts GeoTrust's application-data deck.gl layers onto the existing
 * Mapbox basemap via the shared `DeckOverlayController`, driven entirely
 * by the normalised `MapDataContext` state.
 *
 * Responsibilities:
 *   - Own one named layer group ("spatial-objects" in Phase 1) on the
 *     per-map shared overlay.
 *   - Rebuild layers from state via the pure `layerRegistry`.
 *   - Track basemap zoom for zoom-dependent visibility/detail.
 *   - Route deck picking (hover/click) into the map-data state, and let
 *     the click drive the EXISTING spatial-object detail panel (no
 *     second UI).
 *   - Render lightweight loading / empty / error / permission-denied
 *     status affordances.
 *
 * deck.gl is a pure renderer here — authorization + filtering stay
 * server-side in GeoLink. This component never decides what a user may
 * see; it only draws what the map-data state already holds.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { PickingInfo } from '@deck.gl/core';
import {
  getOrCreateDeckOverlay,
  type DeckOverlayController,
} from '../../services/spatial/deck/deckOverlay';
import { buildDeckLayers, DECK_LAYER_IDS } from '../../services/spatial/deck/layerRegistry';
import { useMapData, type MapEntityType } from './MapDataContext';
import type { SpatialObjectFeature } from '../../services/spatial/geoLinkTypes';
import type { MapFeature } from '../../services/spatial/map/mapTypes';

/** Best-effort stable id for an aggregate map feature. */
function entityIdOf(f: MapFeature | null | undefined): string {
  if (!f) return '';
  const p = (f.properties ?? {}) as Record<string, unknown>;
  return String(
    f.id ?? p.id ?? p.objectId ?? p.code ?? p.countryCode ?? p.agentId ?? p.droneId ?? p.ruleId ?? '',
  );
}

const GROUP_ID = 'spatial-objects';

export interface SpatialDeckOverlayProps {
  /** The host's native map instance (mapbox-gl / maplibre-gl). */
  map: unknown;
  /** Master enable flag (mirrors SpatialObjectsContext view.enabled). */
  enabled?: boolean;
  /** Reduced-detail mode for low-powered devices (disables extrusion). */
  reducedDetail?: boolean;
  /** Interleave with basemap 3D (WebGL2 only; falls back automatically). */
  interleaved?: boolean;
  /** Hide the small status badge (loading/empty/error) — default false. */
  hideStatus?: boolean;
  /**
   * Whether this overlay owns the spatial-objects layer. False when the
   * legacy pipeline renders spatial objects via native fill-extrusion and
   * the overlay is present only for the aggregate layers.
   */
  renderSpatialObjects?: boolean;
}

type ZoomMap = {
  getZoom?: () => number;
  on?: (ev: string, h: () => void) => void;
  off?: (ev: string, h: () => void) => void;
};

const SpatialDeckOverlay: React.FC<SpatialDeckOverlayProps> = ({
  map,
  enabled = true,
  reducedDetail = false,
  interleaved = false,
  hideStatus = false,
  renderSpatialObjects = true,
}) => {
  const { state, selectSpatialObject, hoverSpatialObject, selectEntity, hoverEntity } = useMapData();
  const controllerRef = useRef<DeckOverlayController | null>(null);
  const [zoom, setZoom] = useState<number>(() => {
    try { return (map as ZoomMap)?.getZoom?.() ?? 2; } catch { return 2; }
  });

  // ── Track basemap zoom for zoom-dependent visibility/detail ──
  useEffect(() => {
    const native = map as ZoomMap;
    if (!native || typeof native.on !== 'function' || typeof native.off !== 'function') return;
    const handler = () => {
      try {
        const z = native.getZoom?.();
        if (typeof z === 'number' && Number.isFinite(z)) setZoom(z);
      } catch { /* ignore */ }
    };
    handler();
    native.on('zoom', handler);
    native.on('moveend', handler);
    return () => {
      try { native.off?.('zoom', handler); } catch { /* ignore */ }
      try { native.off?.('moveend', handler); } catch { /* ignore */ }
    };
  }, [map]);

  // ── Picking handlers ──
  const onHover = useCallback((info: PickingInfo) => {
    const feature = (info?.object ?? null) as SpatialObjectFeature | null;
    hoverSpatialObject(feature && feature.geometry ? feature : null);
    // Cursor affordance.
    try {
      const canvas = (map as { getCanvas?: () => HTMLCanvasElement })?.getCanvas?.();
      if (canvas) canvas.style.cursor = feature ? 'pointer' : '';
    } catch { /* ignore */ }
  }, [hoverSpatialObject, map]);

  const onClick = useCallback((info: PickingInfo) => {
    const feature = (info?.object ?? null) as SpatialObjectFeature | null;
    if (!feature || !feature.geometry) {
      selectSpatialObject(null);
      return;
    }
    const anchor = (typeof info.x === 'number' && typeof info.y === 'number')
      ? { x: info.x, y: info.y }
      : undefined;
    selectSpatialObject(feature, anchor);
  }, [selectSpatialObject]);

  // ── Picking handlers for the aggregate map-API layers ──
  const onEntityHover = useCallback((info: PickingInfo, _layerId: string, entityType: MapEntityType) => {
    const feature = (info?.object ?? null) as MapFeature | null;
    hoverEntity(feature ? { entityType, id: entityIdOf(feature) } : null);
    try {
      const canvas = (map as { getCanvas?: () => HTMLCanvasElement })?.getCanvas?.();
      if (canvas) canvas.style.cursor = feature ? 'pointer' : '';
    } catch { /* ignore */ }
  }, [hoverEntity, map]);

  const onEntityClick = useCallback((info: PickingInfo, _layerId: string, entityType: MapEntityType) => {
    const feature = (info?.object ?? null) as MapFeature | null;
    if (!feature) { selectEntity(null); return; }
    const anchor = (typeof info.x === 'number' && typeof info.y === 'number')
      ? { x: info.x, y: info.y }
      : undefined;
    selectEntity({ entityType, id: entityIdOf(feature), entityFeature: feature, anchor });
  }, [selectEntity]);

  // ── Build + push layers whenever the inputs change ──
  useEffect(() => {
    if (!map) return;
    const controller = getOrCreateDeckOverlay(map, { interleaved });
    controllerRef.current = controller;

    const layers = buildDeckLayers({
      state,
      zoom,
      reducedDetail,
      spatialEnabled: enabled,
      renderSpatialObjects,
      onHover,
      onClick,
      onEntityHover,
      onEntityClick,
    });
    controller.setGroup(GROUP_ID, layers);
  }, [map, state, zoom, reducedDetail, enabled, interleaved, onHover, onClick, onEntityHover, onEntityClick, renderSpatialObjects]);

  // ── Teardown: drop our group; dispose the overlay if nothing's left ──
  useEffect(() => {
    return () => {
      const controller = controllerRef.current;
      if (!controller) return;
      controller.clearGroup(GROUP_ID);
      if (!controller.hasGroups()) {
        // Leave disposal to the map-owner path (disposeDeckOverlay) so a
        // StrictMode remount can reuse the overlay; clearing the group is
        // enough to stop drawing our layers.
      }
    };
  }, []);

  // The status badge tracks the spatial-objects fetch. When this overlay
  // doesn't own that layer (legacy pipeline → native extrusion), stay quiet.
  if (hideStatus || !renderSpatialObjects) return null;

  const { loading, error, permissionDenied, empty } = state.status;
  const spatialVisible = state.visibleLayerIds.includes(DECK_LAYER_IDS.spatialObjects) && enabled;

  let badge: string | null = null;
  if (!spatialVisible) badge = null;
  else if (permissionDenied) badge = 'Some map data is restricted for your role';
  else if (error) badge = `Map data error: ${error}`;
  else if (loading && state.spatialObjects.length === 0) badge = 'Loading spatial objects…';
  else if (empty) badge = 'No spatial objects in view';

  if (!badge) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: 12,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 5,
      }}
      aria-live="polite"
    >
      <div
        style={{
          background: permissionDenied || error ? 'rgba(127,29,29,0.92)' : 'rgba(17,24,39,0.85)',
          color: '#f8fafc',
          fontSize: 12,
          fontWeight: 500,
          padding: '6px 12px',
          borderRadius: 9999,
          boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
          border: '1px solid rgba(255,255,255,0.12)',
          maxWidth: '80%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {badge}
      </div>
    </div>
  );
};

export default SpatialDeckOverlay;
